import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { FileText, Calendar, CheckCircle, Eye, X } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const ToolboxTalkSigningModal = () => {
  const { user } = useContext(UserContext);
  const [talksToSign, setTalksToSign] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  console.log('🎭 ToolboxTalkSigningModal rendered, user:', user?.id, 'showModal:', showModal, 'talksToSign:', talksToSign.length);

  useEffect(() => {
    console.log('🔄 useEffect triggered, user:', user?.id);
    if (user && user.id) {
      fetchTalksToSign();
    }
  }, [user]);

  const fetchTalksToSign = async () => {
    if (!user?.id) {
      console.log('⚠️ No user ID, skipping fetch');
      return;
    }

    try {
      setLoading(true);
      console.log('🔍 Fetching toolbox talks to sign for user:', user.id);
      console.log('📌 User details:', { id: user.id, email: user.email, accessToken: user.accessToken ? 'present' : 'missing' });

      const response = await axios.get(`${API_BASE_URL}/toolbox-talks/to-sign/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });

      console.log('📋 Response from /to-sign/:', response.data);
      console.log('📋 Total talks received:', response.data.length);

      // Filter only unsigned talks
      const unsigned = response.data.filter(talk => !talk.has_signed);
      console.log('✍️ Unsigned talks:', unsigned);
      console.log('✍️ Number of unsigned talks:', unsigned.length);

      setTalksToSign(unsigned);

      // Auto-show modal if there are unsigned talks
      if (unsigned.length > 0) {
        console.log('🚀 Showing signing modal with', unsigned.length, 'talks');
        setShowModal(true);
      } else {
        console.log('✅ No unsigned talks to show');
      }
    } catch (error) {
      console.error('❌ Error fetching talks to sign:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
    } finally {
      setLoading(false);
    }
  };

  const handleSignTalk = async (talkId) => {
    try {
      // Use user's predefined signature
      const signature = user.user_signature || '';

      console.log('✍️ Signing talk ID:', talkId);

      await axios.post(
        `${API_BASE_URL}/toolbox-talks/${talkId}/sign/`,
        { signature },
        {
          headers: { Authorization: `Bearer ${user.accessToken}` }
        }
      );

      console.log('✅ Successfully signed talk ID:', talkId);

      // Remove from unsigned list
      setTalksToSign(prev => {
        const updated = prev.filter(t => t.id !== talkId);
        console.log('📝 Remaining unsigned talks:', updated.length);

        // Close modal if no more talks to sign
        if (updated.length === 0) {
          console.log('🎉 All talks signed! Closing modal...');
          setShowModal(false);
        }

        return updated;
      });

      alert('Toolbox Talk signed successfully!');
    } catch (error) {
      console.error('❌ Error signing toolbox talk:', error);
      alert(error.response?.data?.error || 'Failed to sign toolbox talk');
    }
  };

  const handlePreviewPDF = async (talk) => {
    try {
      const url = `${API_BASE_URL}/toolbox-talks/${talk.id}/pdf/?preview=true`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
        responseType: 'blob'
      });

      // Create blob URL and open in new tab
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');

      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error previewing PDF:', error);
      alert('Failed to preview PDF');
    }
  };

  // Don't render anything if no talks to sign or modal is closed
  if (!showModal || talksToSign.length === 0) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 animate-fadeIn"
        onClick={() => setShowModal(false)}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-slideInRight">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white rounded-full p-2">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Toolbox Talks to Sign</h2>
                <p className="text-blue-100 text-sm">You have {talksToSign.length} toolbox talk(s) awaiting your signature</p>
              </div>
            </div>
            <button
              onClick={() => setShowModal(false)}
              className="text-white hover:bg-blue-800 rounded-full p-2 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
            <div className="space-y-4">
              {talksToSign.map((talk) => (
                <div key={talk.id} className="border-2 border-blue-200 rounded-lg p-4 hover:border-blue-400 transition-all bg-gradient-to-r from-blue-50 to-white">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 mb-2">{talk.topic}</h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(talk.date).toLocaleDateString()}</span>
                          {talk.time && <span className="ml-1">at {talk.time}</span>}
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <FileText className="w-4 h-4" />
                          <span>{talk.project}</span>
                        </div>
                        <div className="col-span-2 text-gray-600">
                          <span className="font-medium">Supervisor:</span> {talk.conductor}
                        </div>
                        {talk.work_activities && talk.work_activities.length > 0 && (
                          <div className="col-span-2">
                            <span className="font-medium text-gray-700">Work Activities:</span>
                            <ul className="list-disc list-inside ml-2 mt-1 text-gray-600">
                              {talk.work_activities.map((activity, idx) => {
                                // Support both old string format and new object format
                                const activityName = typeof activity === 'string' ? activity : activity.name;
                                const activityTurbine = typeof activity === 'string' ? '' : (activity.turbine || '');
                                return (
                                  <li key={idx} className="text-sm">
                                    {activityName}
                                    {activityTurbine && <span className="text-gray-400 ml-1">({activityTurbine})</span>}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}

                        {/* Subjects Discussed */}
                        {(() => {
                          const subjects = [];
                          const subjectFields = {
                            type_of_operation: "Type of Operation",
                            crane_requirements: "Crane Requirements",
                            methods_procedures: "Methods/Procedures",
                            potential_hazards: "Potential Hazards",
                            permit_to_work: "Permit to Work",
                            equipment_to_be_used: "Equipment to be Used",
                            safety_spill_equipment: "Safety/Spill Equipment",
                            materials_chemicals: "Materials/Chemicals",
                            responsibilities: "Responsibilities",
                            isolations_necessary: "Isolations Necessary",
                            access_egress: "Access/Egress",
                            impact_adjacent_work: "Impact on Adjacent Work",
                            housekeeping: "Housekeeping",
                            weather_conditions: "Weather Conditions",
                            emergency_response: "Emergency Response",
                            communication: "Communication",
                            incident_near_miss: "Incident/Near Miss",
                            other: "Other"
                          };

                          Object.keys(subjectFields).forEach(key => {
                            if (talk[key]) subjects.push(subjectFields[key]);
                          });

                          return subjects.length > 0 && (
                            <div className="col-span-2 mt-2">
                              <span className="font-medium text-gray-700">Subjects Discussed:</span>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {subjects.map((subject, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                    {subject}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {talk.hse_comments && (
                          <div className="col-span-2 mt-2">
                            <span className="font-medium text-gray-700">HSE Comments:</span>
                            <p className="text-sm text-gray-600 mt-1">{talk.hse_comments}</p>
                          </div>
                        )}

                        {talk.hazards && (
                          <div className="col-span-2 mt-2">
                            <span className="font-medium text-gray-700">Hazards:</span>
                            <p className="text-sm text-gray-600 mt-1">{talk.hazards}</p>
                          </div>
                        )}

                        {talk.preventive_measures && (
                          <div className="col-span-2 mt-2">
                            <span className="font-medium text-gray-700">Preventive Measures:</span>
                            <p className="text-sm text-gray-600 mt-1">{talk.preventive_measures}</p>
                          </div>
                        )}

                        {talk.notes && (
                          <div className="col-span-2 mt-2">
                            <span className="font-medium text-gray-700">Notes:</span>
                            <p className="text-sm text-gray-600 mt-1">{talk.notes}</p>
                          </div>
                        )}

                        {talk.custom_fields && Array.isArray(talk.custom_fields) && talk.custom_fields.length > 0 && (
                          <div className="col-span-2 mt-2">
                            <span className="font-medium text-gray-700">Additional Fields:</span>
                            <div className="mt-1 space-y-1">
                              {talk.custom_fields.map((field, idx) => (
                                <div key={idx} className="text-sm text-gray-600">
                                  <span className="font-medium">{field.label}:</span> {field.value || '-'}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleSignTalk(talk.id)}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-all flex items-center gap-2 shadow-sm hover:shadow-md font-medium"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Sign Now
                      </button>
                      <button
                        onClick={() => handlePreviewPDF(talk)}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-all flex items-center gap-2 text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        Preview
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!user.user_signature && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="text-yellow-600 mt-0.5">⚠️</div>
                  <div>
                    <p className="font-medium text-yellow-900">No signature defined</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      You don't have a signature set up in your profile. Please go to your user settings to add your signature before signing toolbox talks.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ToolboxTalkSigningModal;
